import { useCallback, useState } from 'react';

export type ChatConversationState = {
  memoryId: string | null;
  etag: string | null;
  turnCount: number;
  parametersLocked: boolean;
  streamingText: string;
  /** Whether the user has sent their first interactive message (distinct from seed prompt turn). */
  userHasInteracted: boolean;
};

const initialState: ChatConversationState = {
  memoryId: null,
  etag: null,
  turnCount: 0,
  parametersLocked: false,
  streamingText: '',
  userHasInteracted: false,
};

export function useChatTestRun() {
  const [chatState, setChatState] = useState<ChatConversationState>(initialState);

  const completeFirstTurn = useCallback((memoryId: string, etag: string | null) => {
    setChatState(prev => ({
      ...prev,
      memoryId,
      etag,
      turnCount: 1,
      parametersLocked: true,
      streamingText: '',
    }));
  }, []);

  const completeSubsequentTurn = useCallback((memoryId: string, etag: string | null) => {
    setChatState(prev => {
      // Derive turn count from ETag (format: "{memory_id}-{turn}") for accuracy.
      // This is authoritative — immune to duplicate completion callbacks.
      let turnCount = prev.turnCount + 1;
      if (etag) {
        const lastDash = etag.lastIndexOf('-');
        if (lastDash > 0) {
          const parsed = parseInt(etag.slice(lastDash + 1), 10);
          if (!isNaN(parsed)) turnCount = parsed;
        }
      }
      return {
        ...prev,
        memoryId,
        etag,
        turnCount,
        parametersLocked: true,
        streamingText: '',
      };
    });
  }, []);

  const appendStreamingText = useCallback((chunk: string) => {
    setChatState(prev => ({
      ...prev,
      streamingText: prev.streamingText + chunk,
    }));
  }, []);

  const clearStreamingText = useCallback(() => {
    setChatState(prev => ({ ...prev, streamingText: '' }));
  }, []);

  const markUserInteracted = useCallback(() => {
    setChatState(prev => ({ ...prev, userHasInteracted: true }));
  }, []);

  const restoreConversation = useCallback((memoryId: string, etag: string | null) => {
    // Parse turn count from ETag format "{memory_id}-{turn}"
    let turnCount = 1;
    if (etag) {
      const lastDash = etag.lastIndexOf('-');
      if (lastDash > 0) {
        const parsed = parseInt(etag.slice(lastDash + 1), 10);
        if (!isNaN(parsed)) turnCount = parsed;
      }
    }

    setChatState({
      memoryId,
      etag,
      turnCount,
      parametersLocked: true,
      streamingText: '',
      userHasInteracted: true,
    });
  }, []);

  const reset = useCallback(() => {
    setChatState(initialState);
  }, []);

  return {
    chatState,
    completeFirstTurn,
    completeSubsequentTurn,
    appendStreamingText,
    clearStreamingText,
    markUserInteracted,
    restoreConversation,
    reset,
  };
}
